import React, { useRef, useEffect, useState } from 'react';
import Button from './ui/Button';
import Language from './ui/Language';
import { fetchChatGPT } from './chatGPT';
import { postData } from '../utils/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMicrophone,
  faCheck,
  faSpinner,
  faClipboard,
  faLightbulb,
  faTrashCan,
  faCircleStop,
  faX,
  faPaperPlane,
  faWandMagicSparkles
} from '@fortawesome/free-solid-svg-icons';

import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';

const lookUp = {
  microphone: faMicrophone,
  check: faCheck,
  spinner: faSpinner,
  clipboard: faClipboard,
  lightbulb: faLightbulb,
  trashcan: faTrashCan,
  stop: faCircleStop,
  x: faX,
  send: faPaperPlane,
  magic: faWandMagicSparkles
};

const Notes = ({ eventId, closeNote, people, staffMember }) => {
  const textAreaRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');
  const [concept, setConcept] = useState('');
  
  const icon = (name) => name === 'spinner' ? <FontAwesomeIcon icon={lookUp[name]} spin /> : <FontAwesomeIcon icon={lookUp[name]} />;

  // Speech system
  const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } =
    useSpeechRecognition();

  useEffect(() => {
    let currentNote = localStorage.getItem(eventId);
    let currentConcept = localStorage.getItem(`${eventId}-concept`);
    if (currentNote) setNote(currentNote);
    if (currentConcept) setConcept(currentConcept);
  }, []);

  useEffect(() => {
    if (note.length === 0) resetTranscript();
    localStorage.setItem(eventId, note);
    setCopied(false);
  }, [note]);

  useEffect(() => {
    if (note.length === 0 && concept) addConcept(concept);
    textAreaRef.current.focus();
    localStorage.setItem(`${eventId}-concept`, concept);
  }, [concept]);

  const startSpeach = () => {
    SpeechRecognition.startListening();
  };

  const stopSpeach = () => {
    SpeechRecognition.stopListening();
  };

  const clear = () => {
    resetTranscript();
    setNote('');
    setConcept('');
    localStorage.removeItem(eventId);
    localStorage.removeItem(`${eventId}-concept`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!window.confirm('Are you sure you want to submit this note?')) return;

    let noteJSON = {
      note: {
        note: note,
        public: true,
        send_notifications: true,
      },
    };
    postData(`/desk/event_occurrences/${eventId}/notes`, noteJSON)
      .then((data) => {
        console.log(data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const copy = () => {
    navigator.clipboard.writeText(note);
    setCopied(true);
  };

  const generate = async () => {
    setLoading(true);
    const attendeeStr = people.map((person) => person.name).join(', ');
    setNote('Your new generated note is loading! Please wait about 10 seconds...\n\nHere is what you inputed into the generator:\n\n' + note);
  
    try {
      const generatedNote = await fetchChatGPT(attendeeStr, staffMember, note, concept);
      setNote(generatedNote);
    } catch (error) {
      console.log('error', error);
    } finally {
      setLoading(false);
    }
  };

  const addConcept = (concept) => {
    setConcept(concept);
    let newLine = note.length > 0 && note[note.length - 1] !== `\n`;
    setNote(note + (newLine ? `\n` : '') + 'worked on our project today with ' + concept + `\n`);
  };

  const languages = ['python', 'scratch', 'roblox', 'cpp', 'html', 'css', 'javascript', 'react', 'unity', 'godot', 'blender', 'swift'];
  const languageButtons = languages.map((language) => <Language language={language} onClick={() => addConcept(language)} />);

  return (
    <div className="flex flex-col gap-4 mt-4" onClick={(e) => e.stopPropagation()}>
      {concept
      ?
      <div className='w-1/4 sm:w-1/12'>
        <Language language={concept} />
      </div>
      :
      <div className="grid grid-cols-4 sm:grid-cols-12 gap-2 w-full">
        {languageButtons}
      </div>}
      <textarea
        className={`w-full h-96 py-2 px-4 resize-y
                    border border-gray-300 rounded-lg
                    text-black outline-none`}
        placeholder={concept ? "Type your note here..." : "Please select a language first."}
        value={note ? note : transcript}
        onChange={(e) => setNote(e.target.value)}
        ref={textAreaRef}
        disabled={loading || !concept}
      />
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 w-full">
        <Button
            onClick={generate}
            icon={loading ? icon('spinner') : icon('magic')}
            text="Generate"
            type="special"
            disabled={note.trim().split(' ').length < 5 || loading}
          />
        <Button
          onClick={copy}
          icon={copied ? icon('check') : icon('clipboard')}
          text={copied ? 'Copied!' : 'Copy'}
          type="success"
          disabled={note.length === 0}
        />
        <Button
          onClick={handleSubmit}
          icon={icon('send')}
          text="Submit"
          type="primary"
          disabled={note.length === 0}
        />
        <Button
          onClick={clear}
          icon={icon('trashcan')}
          text="Clear"
          type="warning"
          disabled={note.length === 0}
        />
      </div>
    </div>
  );
};

export default Notes;
